import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { ConfirmDialog } from '~/components/ui/Modal.js';
import { Spinner } from '~/components/ui/Spinner.js';
import { ProjectForm } from '~/features/projects/ProjectForm.js';
import { useDeleteProject, useProject } from '~/features/projects/hooks.js';
import { useSectorsList } from '~/features/taxonomies/hooks.js';
import { ApiError } from '~/lib/api.js';

export const Route = createFileRoute('/_auth/projects/$slug')({
  component: ProjectEditPage,
});

function ProjectEditPage() {
  const { slug } = Route.useParams();
  const navigate = Route.useNavigate();

  const proj = useProject(slug);
  const sectors = useSectorsList();
  const del = useDeleteProject();

  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!proj.data) return;
    try {
      await del.mutateAsync(proj.data.id);
      toast.success('Projeto removido.');
      setDeleting(false);
      navigate({ to: '/projects' });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao remover.');
    }
  }

  if (proj.isLoading || sectors.isLoading) {
    return (
      <div className="page">
        <div className="dt__state">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (proj.error || !proj.data) {
    return (
      <div className="page">
        <header className="page-head">
          <p className="eyebrow">/ Erro</p>
          <h1 className="page-title">Projeto não encontrado.</h1>
          <p className="page-sub">{proj.error?.displayMessage ?? 'Slug inválido.'}</p>
        </header>
        <Link to="/projects">
          <Button variant="secondary">Voltar à lista</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Portfólio</p>
        <h1 className="page-title">{proj.data.nome}</h1>
        <p className="page-sub">
          <span style={{ color: 'var(--fg-muted)' }}>
            {proj.data.municipio} / {proj.data.estadoUf} · <code>{proj.data.slug}</code>
          </span>
        </p>
        <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          {proj.data.exibirHome && <Badge tone="success">no destaque da home</Badge>}
          {proj.data.exibirMapa && <Badge tone="primary">no mapa</Badge>}
          {!proj.data.exibirHome && !proj.data.exibirMapa && (
            <Badge tone="muted">não exibido no site</Badge>
          )}
        </div>
      </header>

      {sectors.data && <ProjectForm project={proj.data} sectors={sectors.data} />}

      <section className="form-card form-card--danger">
        <h2 className="form-card__title">Ações sensíveis</h2>
        <p className="form-card__sub">
          A exclusão é permanente. Para esconder do site sem perder o registro, edite e desligue
          "Exibir mapa" e "Exibir home".
        </p>
        <div className="form-actions form-actions--gap">
          <Button variant="danger" onClick={() => setDeleting(true)}>
            Excluir projeto
          </Button>
        </div>
      </section>

      <Link to="/projects">
        <Button variant="ghost">← Voltar à lista</Button>
      </Link>

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={handleDelete}
        title="Excluir projeto?"
        description={`O projeto "${proj.data.nome}" será removido permanentemente.`}
        confirmLabel="Sim, excluir"
        variant="danger"
        loading={del.isPending}
      />
    </div>
  );
}
